<template>
  <div :class="$style.home">
    <VideoPlayer :options="videoOptions" v-if="status && fileType === 'video'" />
    <div :class="$style.audioPlayer" v-else-if="status && fileType === 'audio'">
      <AudioPlayer :options="audioOptions" />
    </div>
  </div>
</template>

<script>
import { GetFileInfoByFileId, GetMediaPreview, GetFileInfoBySid, getStarStatus, getFilePath } from '@/services/api'
import { mapState, mapMutations } from 'vuex'
import Emitter from 'element-ui/lib/mixins/emitter'
import VideoPlayer from '@/components/VideoPlayer'
import AudioPlayer from '@/components/AudioPlayer'
import merge from 'lodash/merge'
import EventBus from '@/utils/EventBus'
import AddRoamingRecord from '@/utils/command/addRoamingRecord'
import { errorCodeMap } from '@/utils/errorcodes.js'
import { formatApiRequestBaseUrl } from '@/utils/tools'

export default {
  name: 'PC-Home',
  components: { VideoPlayer, AudioPlayer },
  computed: mapState(['fileType']),
  mixins: [Emitter],
  data() {
    return {
      fileInfo: null,
      videoOptions: null,
      audioOptions: null,
      // 加载状态是否成功
      status: false
    }
  },
  beforeMount() {
    // 重写分享连接带了文件名过来的情况
    const fid = this.$route.params.fileId || this.$route.params.sid
    let fType = ''
    if (this.$route.params.fileId) {
      fType = 'fileId'
    }
    if (this.$route.params.sid) {
      fType = 'sid'
    }

    let pathMatch = location.href.match(/(.*[&?]f=\w+)/)
    if (pathMatch) {
      try {
        history.replaceState({}, null, pathMatch[0])
      } catch (err) { }
      return
    }

    if (fType) {
      let fidArr = fid.split(' ')
      let newFid = ''
      if (fidArr.length > 1) {
        newFid = fidArr[0]
      }
      let fidMatch = fid.match(/(\w+)(.*)/)
      if (fidMatch && fidMatch.length > 2) {
        newFid = fidMatch[1]
      }
      if (newFid) {
        this.$router.replace({ name: this.$route.name, params: merge(this.$route.params, { [fType]: newFid }), query: this.$route.query })
      }
    }
  },
  async mounted() {
    // 解析地址栏的fileid，也有可能是sid
    let fileId = this.$route.params.fileId
    let sid = this.$route.params.sid
    let metaDataError = false

    if (!fileId && !sid) {
      this.$emit('getErrorMsg')
      return
    }
    let fileInfo = fileId ? await GetFileInfoByFileId(fileId).catch(({ response }) => {
      this.dispatch('APP', 'set_loading_status', false)
      this.errorHandle(response)
      metaDataError = true
    }) : await GetFileInfoBySid(sid).catch(({ response }) => {
      this.dispatch('APP', 'set_loading_status', false)
      this.errorHandle(response)
      metaDataError = true
    })

    let fname = ''
    if (fileInfo && fileInfo.fileinfo) {
      fname = fileInfo.fileinfo.fname
    }
    if (metaDataError) {
      this.SET_FAVICON()
    } else {
      this.SET_FILE_TYPE(fname)
    }

    if (!fileInfo) return
    this.fileInfo = fileInfo.fileinfo
    this.SET_FILE_INFO(this.fileInfo)
    new AddRoamingRecord(this.fileInfo).execute()

    // 获取星标状态
    // eslint-disable-next-line camelcase
    getStarStatus(this.fileInfo.fileid).then(({ item_map }) => {
      this.SET_FILE_STAR(!!Object.keys(item_map).length)
    })

    // 获取文件路径
    getFilePath(this.fileInfo.groupid, this.fileInfo.fileid).then(res => {
      this.SET_FILE_PATH(res)
    })

    // 获取媒体信息
    const mediaInfo = await this.tryLoad()

    this.dispatch('APP', 'set_loading_status', false)
    if (!mediaInfo) {
      return
    }

    this.status = true
    if (mediaInfo.format_type === 'video') {
      this.videoOptions = this.videoOptionsBuilder(formatApiRequestBaseUrl(mediaInfo.stream_path), mediaInfo.video_cover)
    } else if (mediaInfo.format_type === 'audio') {
      this.audioOptions = this.audioOptionsBuilder(formatApiRequestBaseUrl(mediaInfo.stream_path))
    }
    this.$emit('getSuccess')

    if (this.fileType === 'unknown') {
      this.dispatch('APP', 'showError', { info: { code: errorCodeMap.FILETYPE_NOT_SUPPORT } })
    }
  },
  methods: {
    ...mapMutations(['SET_FILE_TYPE', 'SET_FILE_INFO', 'SET_FAVICON', 'SET_FILE_STAR', 'SET_FILE_PATH']),
    errorHandle(res) {
      this.$emit('getErrorMsg', res && res.data)
    },
    videoOptionsBuilder(url, poster) {
      return {
        textTrackDisplay: false,
        autoplay: false,
        controls: true,
        aspectRatio: '16:9',
        poster: poster,
        sources: [{
          src: url
        }],
        html5: {
          hls: {
            maxBufferLength: 30,    // 最大缓冲长度（秒）
            maxMaxBufferLength: 60, // 最大可能的缓冲长度
            minBufferLength: 5,     // 开始播放所需的最小缓冲
            highWaterMark: 2 * 1024 * 1024 // 网络请求缓冲区大小（2MB）
          }
        },
        controlBar: {
          children: {
            playToggle: true,
            progressControl: true,
            currentTimeDisplay: true,
            timeDivider: true,
            durationDisplay: true,
            volumePanel: {
              inline: false
            },
            fullscreenToggle: true
          }
        },
        language: 'zh-CN'
      }
    },
    audioOptionsBuilder(url) {
      return { url }
    },
    async tryLoad() {
      let data = null
      let previewType = this.$route.params.sid ? 'link' : 'normal'
      while (true) {
        try {
          this.$DwManager.measureRequestLoadTime('start')
          const res = await GetMediaPreview(this.fileInfo.groupid, this.fileInfo.fileid, previewType).catch((err) => {
            // 当接口报错时，展示服务端返回的错误信息
            this.dispatch('APP', 'showError', { info: {requestType: 'preview', ...(err.detail?.info || {})} })
            return false
          })
          if (!res || !res.data) {
            return
          }

          data = res.data
          if (data) {
            if (!data.time_wait) {
              this.$DwManager.measureRequestLoadTime('end')
              this.$DwManager.reportMediaApiAction({
                type: data.format_type
              })
              break
            }

            if (data.time_wait && data.progress) {
              EventBus.$emit('updateProcessing', data.progress)
            }
          }
          await new Promise(resolve => setTimeout(resolve, data.time_wait * 1000))
        } catch (err) {
          this.errorHandle(err.response)
          this.$DwManager.measureRequestLoadTime('end')
          this.$DwManager.reportMediaApiAction({
            is_success: false
          })
          break
        }
      }

      return data
    }
  }
}
</script>

<style lang="scss" module>
.home {
  flex: 1;
  overflow: hidden;
}

.audioPlayer {
  display: flex;
  height: 100%;
  justify-content: center;
  align-items: center;
}
</style>
